import { HubConnectionBuilder } from '@microsoft/signalr'
import {
    useQuery,
    experimental_streamedQuery as streamedQuery,
    useQueryClient,
    keepPreviousData,
} from '@tanstack/react-query'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { useGrpcClients } from '@/contexts/GrpcClientContext'
import { TrackingSortBy } from '@/generated/Enumerations/TrackingSortBy_pb'
import { parseId } from '@/helper/id'
import {
    listQueryKeyPrefix,
    notificationQueryKeyPrefix,
} from '@/helper/queryKeyPrefixes'
import { trackingsQueryOptions } from '@/queries/useTrackings'

const notificationsQueryKey = [notificationQueryKeyPrefix, listQueryKeyPrefix]

export const useNotifications = () => {
    const { user } = useAuth()

    const { t } = useTranslation()

    const abortedTrackingsGenerator = React.useMemo(() => {
        const hub = new HubConnectionBuilder()
            .withUrl(`${import.meta.env.VITE_BACKEND_URL}/notifications`, {
                accessTokenFactory: () => user?.access_token ?? '',
            })
            .withAutomaticReconnect()
            .build()

        // Wrap the signalr hub in a generator, so it works better with useQuery
        return async function* () {
            await hub.start()

            // Create a new promise that will be used to change the callback.
            let newId = Promise.withResolvers<string>()

            hub.on('TrackingTerminated', (id: string) => {
                // Resolve promise, whenever the function was called on the backend.
                newId.resolve(id)
            })

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            while (true) {
                // Wait until data arrives and yield it.
                yield await newId.promise

                // Create a new promise for the next value.
                newId = Promise.withResolvers<string>()

                // Show a desktop notification.
                const permission =
                    Notification.permission === 'granted' ?
                        Notification.permission
                    :   await Notification.requestPermission()
                if (permission !== 'granted') {
                    continue
                }

                new Notification(t('notifications.trackingAborted'), {
                    icon: '/hourglass.png',
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const { data: ids = [] } = useQuery({
        queryKey: notificationsQueryKey,
        queryFn: streamedQuery({
            streamFn: abortedTrackingsGenerator,
            refetchMode: 'append',
        }),
    })

    const clients = useGrpcClients()

    const res = useQuery({
        ...trackingsQueryOptions(clients, {
            ids: ids.map(parseId),
            userIds: user?.profile.sub ? [parseId(user.profile.sub)] : [],
            sorting: [{ sortBy: TrackingSortBy.STARTED_AT, descending: true }],
        }),
        enabled: ids.length > 0,
        placeholderData: ids.length > 0 ? keepPreviousData : undefined,
    })

    const queryClient = useQueryClient()

    return {
        res,
        markAsSeen: (id: string) =>
            queryClient.setQueryData(notificationsQueryKey, (d: string[]) =>
                d.filter(dId => dId !== id)
            ),
    }
}
